const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The pay stub forms' Tax Settings start from default rates. The built-ins
// below are the numbers the generators have always used; the admin can
// replace them per year (Tax Layouts -> Tax rates), and the forms seed
// their rate inputs from whichever applies to the stub's year. What the
// user finally types in the form is what the generators calculate with.

// futa is the employer's federal unemployment rate; state unemployment has
// no single built-in (blank = the per-state new-employer table)
export const US_BUILTIN_RATES = { federal: 22, socialSecurity: 6.2, medicare: 1.45, futa: 0.6 };
export const CA_BUILTIN_RATES = { cpp: 5.95, ei: 1.64 }; // federal + provincial default to bracket math
export const UK_RATE_KEYS = ["paye", "ni"]; // both default to the tax-code band / NI threshold maths

let cache = null; // { at, rates: [{country, year, rates}] }
const TTL = 60_000;

export async function fetchTaxRates() {
  if (cache && Date.now() - cache.at < TTL) return cache.rates;
  try {
    const res = await fetch(`${BACKEND_URL}/api/tax-rates`);
    const d = await res.json();
    cache = { at: Date.now(), rates: Array.isArray(d.rates) ? d.rates : [] };
  } catch {
    cache = { at: Date.now() - TTL + 5_000, rates: cache ? cache.rates : [] };
  }
  // arm the local-tax lookup with the admin's city taxes, per year
  const byYear = {};
  for (const r of cache.rates) {
    if (r && r.country === "us" && Array.isArray(r.cities) && r.cities.length) byYear[String(r.year)] = r.cities;
  }
  setAdminLocalCities(byYear);
  return cache.rates;
}
export function invalidateTaxRates() { cache = null; }

// The defaults a form seeds its inputs with, for one country + year:
// admin-set values win, the built-ins fill the rest. Values come back as
// strings ready for inputs ("" = automatic bracket math, Canada only).
export async function taxRateDefaultsFor(country, year) {
  const rows = await fetchTaxRates();
  const admin = (rows.find((r) => r.country === country && String(r.year) === String(year)) || {}).rates || {};
  const keys = country === "ca" ? ["federal", "provincial", "cpp", "ei"] : country === "uk" ? UK_RATE_KEYS : ["federal", "socialSecurity", "medicare", "futa", "suta"];
  const out = {};
  for (const k of keys) {
    // Canada's automatic math (brackets, CPP exemption + caps) is the true
    // default, so its inputs stay blank = auto unless the admin set a flat
    // rate; the US flat built-ins prefill outright.
    const v = admin[k] !== undefined ? admin[k] : (country === "us" ? US_BUILTIN_RATES[k] : undefined);
    out[k] = v === undefined ? "" : String(v);
  }
  return out;
}

// The admin's per-state US override for a year, as an input-ready string
// ("" when none is set and the built-in state table applies).
export async function stateRateDefaultFor(state, year) {
  if (!state) return "";
  const rows = await fetchTaxRates();
  const row = rows.find((r) => r.country === "us" && String(r.year) === String(year));
  const v = row && row.states ? row.states[String(state).toUpperCase()] : undefined;
  return v === undefined ? "" : String(v);
}

import { setUsTaxConfig } from "./federalTaxCalculator";
import { setAdminLocalCities } from "./taxRates";

// The admin's bracket rows come as {max, rate-percent}; the calculator
// walks {min, max, rate-fraction} runs.
const normalizeBrackets = (byStatus) => {
  if (!byStatus || typeof byStatus !== "object") return null;
  const out = {};
  for (const [status, rows] of Object.entries(byStatus)) {
    if (!Array.isArray(rows) || rows.length === 0) continue;
    let min = 0;
    out[status] = rows.map((r, i) => {
      const max = r.max == null ? Infinity : Number(r.max);
      const b = { min, max: i === rows.length - 1 ? Infinity : max, rate: Number(r.rate) / 100 };
      min = b.max;
      return b;
    });
  }
  return Object.keys(out).length ? out : null;
};

// Arm the calculator with the admin config for one year (from the warm
// cache; a cold cache means the built-ins and a refresh is kicked off).
export function armUsTaxConfig(year) {
  const rows = cache ? cache.layouts || cache.rates || [] : null;
  if (rows === null) { fetchTaxRates(); setUsTaxConfig(null); return; }
  const row = rows.find((r) => r.country === "us" && String(r.year) === String(year));
  if (!row) { setUsTaxConfig(null); return; }
  setUsTaxConfig({
    brackets: normalizeBrackets(row.brackets),
    deductions: row.deductions && Object.keys(row.deductions).length ? row.deductions : null,
    stateAllowance: row.stateAllowance ?? null,
  });
}

const frac = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.min(60, Math.max(0, n)) / 100 : null;
};

// What the US generators calculate with, straight from the form's Tax
// Settings; the old hardcoded numbers stand in wherever nothing was set.
export function usRates(formData = {}) {
  const year = parseInt(String(formData.endDate || "").slice(0, 4), 10) || new Date().getFullYear();
  armUsTaxConfig(year);
  return {
    ss: frac(formData.socialSecurityRate) ?? 0.062,
    med: frac(formData.medicareRate) ?? 0.0145,
    fedFlat: frac(formData.federalTaxRate) ?? 0.22,
    state: frac(formData.stateTaxRate), // null = the per-state table
    // the employer taxes on the stub's employer section
    futa: frac(formData.futaRate) ?? 0.006,
    suta: frac(formData.sutaRate), // null = the state's new-employer rate
  };
}

// The UK overrides: null means the tax-code band / NI threshold maths; a
// number is a flat percent the user or admin chose.
export function ukOverrides(formData = {}) {
  return {
    paye: frac(formData.payeRate),
    ni: frac(formData.niRate),
  };
}

// The Canadian overrides: null means the bracket/cap math the form has
// always used; a number is a flat percent the user or admin chose.
export function caOverrides(formData = {}) {
  return {
    federal: frac(formData.federalTaxRate),
    provincial: frac(formData.provincialTaxRate),
    cpp: frac(formData.cppRate),
    ei: frac(formData.eiRate),
  };
}
